import { useState, useRef, useMemo } from 'react'
import {
  Files as FilesIcon,
  Upload,
  Download,
  Trash2,
  FileText,
  Image as ImageIcon,
  File,
  FileSpreadsheet,
  FileArchive,
  Eye,
  Edit,
  Filter,
  Search,
  RotateCcw,
  HardDrive,
  FileCode,
  AlertCircle,
} from 'lucide-react'
import {
  useAttachments,
  useUploadAttachment,
  useDeleteAttachment,
  useUpdateAttachment,
} from '@/features/files/api/useFiles'
import type { Attachment } from '@/features/files/api/useFiles'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { formatDate } from '@/lib/utils'
import { toast } from 'sonner'
import { apiClient } from '@/lib/api/client'

// ─── Constants ────────────────────────────────────────────────────────────────
const downloadAttachment = async (id: number, fileName: string) => {
  const response = await apiClient.get(`/api/attachments/${id}/download`, {
    responseType: 'blob',
  })

  const blob = new Blob([response.data])
  const url = window.URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', fileName)

  document.body.appendChild(link)
  link.click()
  link.remove()

  window.URL.revokeObjectURL(url)
}

const ENTITY_TYPES = [
  { value: 'testcase', label: 'Test Cases' },
  { value: 'testplan', label: 'Test Plans' },
  { value: 'requirement', label: 'Requirements' },
  { value: 'execution', label: 'Test Executions' },
  { value: 'build', label: 'Builds' },
  { value: 'defect', label: 'Defects' },
  { value: 'compliance_audit', label: 'Compliance Audits' },
  { value: 'security_scan', label: 'Security Scans' },
] as const

const ACCEPTED_FILE_TYPES = '.jpg,.jpeg,.png,.gif,.pdf,.txt,.csv,.zip,.tar,.tar.gz,.tgz,.docx,.xlsx,.json,.xml,.yaml,.yml,.md,.py,.js,.ts,.go,.rs,.java,.c,.cpp,.h,.rb,.php,.sh,.sql'

const MAX_FILE_SIZE_MB = 50
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getFileIcon(fileType: string) {
  if (fileType.startsWith('image/')) return <ImageIcon className="h-5 w-5 text-blue-500" />
  if (fileType === 'application/pdf') return <FileText className="h-5 w-5 text-red-500" />
  if (fileType.includes('spreadsheet') || fileType.includes('excel') || fileType.includes('csv'))
    return <FileSpreadsheet className="h-5 w-5 text-green-500" />
  if (fileType.includes('zip') || fileType.includes('archive') || fileType.includes('tar') || fileType.includes('gzip'))
    return <FileArchive className="h-5 w-5 text-orange-500" />
  if (fileType.includes('json') || fileType.includes('xml') || fileType.includes('yaml') || fileType.includes('javascript') || fileType.includes('typescript') || fileType.includes('python'))
    return <FileCode className="h-5 w-5 text-purple-500" />
  return <File className="h-5 w-5 text-gray-500" />
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`
}

function getEntityLabel(fkTable: string): string {
  const found = ENTITY_TYPES.find((t) => t.value === fkTable)
  return found ? found.label : fkTable
}

// ─── Loading Skeleton ─────────────────────────────────────────────────────────

function FilesTableSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 p-3">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-5 w-16 rounded-full" />
          <Skeleton className="h-5 w-20 rounded-full" />
          <Skeleton className="h-4 w-28" />
          <div className="ml-auto flex gap-2">
            <Skeleton className="h-8 w-8 rounded" />
            <Skeleton className="h-8 w-8 rounded" />
            <Skeleton className="h-8 w-8 rounded" />
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Summary Stats ────────────────────────────────────────────────────────────

function FileStats({ attachments }: { attachments: Attachment[] }) {
  const stats = useMemo(() => {
    const totalSize = attachments.reduce((sum, a) => sum + (a.file_size || 0), 0)
    const entityGroups = attachments.reduce<Record<string, number>>((acc, a) => {
      const key = a.fk_table || 'unattached'
      acc[key] = (acc[key] || 0) + 1
      return acc
    }, {})
    const imageCount = attachments.filter((a) => a.file_type?.startsWith('image/')).length
    const docCount = attachments.filter(
      (a) => a.file_type?.includes('pdf') || a.file_type?.includes('document') || a.file_type?.includes('word')
    ).length
    const archiveCount = attachments.filter(
      (a) => a.file_type?.includes('zip') || a.file_type?.includes('archive') || a.file_type?.includes('tar')
    ).length

    return { totalSize, entityGroups, imageCount, docCount, archiveCount, total: attachments.length }
  }, [attachments])

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FilesIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total Files</p>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <HardDrive className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{formatFileSize(stats.totalSize)}</p>
              <p className="text-xs text-muted-foreground">Total Storage</p>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <ImageIcon className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.imageCount}</p>
              <p className="text-xs text-muted-foreground">Images</p>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <FileArchive className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.docCount + stats.archiveCount}</p>
              <p className="text-xs text-muted-foreground">Docs &amp; Archives</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function FilesPage() {
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Filters
  const [selectedEntityType, setSelectedEntityType] = useState<string>('')
  const [entityId, setEntityId] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')

  // Upload dialog
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadTitle, setUploadTitle] = useState('')
  const [uploadDescription, setUploadDescription] = useState('')
  const [uploadEntityType, setUploadEntityType] = useState<string>('')
  const [uploadEntityId, setUploadEntityId] = useState<string>('')

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingAttachment, setEditingAttachment] = useState<Attachment | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')

  // Preview dialog
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false)
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null)

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletingAttachment, setDeletingAttachment] = useState<Attachment | null>(null)

  // API hooks — Fetch ALL attachments for tenant (RLS enforces tenant isolation)
  const { data: attachments, isLoading, isError, error, refetch } = useAttachments()
  const uploadMutation = useUploadAttachment()
  const updateMutation = useUpdateAttachment()
  const deleteMutation = useDeleteAttachment()

  // Derived: active filter state
  const hasActiveFilters = !!(selectedEntityType || entityId || searchQuery)

  // Client-side filtering over the tenant's complete attachment list
  const filteredAttachments = useMemo(() => {
    if (!attachments) return []
    return attachments.filter((attachment) => {
      if (selectedEntityType && attachment.fk_table !== selectedEntityType) return false
      if (entityId && attachment.fk_id !== parseInt(entityId, 10)) return false
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        return (
          attachment.file_name?.toLowerCase().includes(q) ||
          attachment.title?.toLowerCase().includes(q) ||
          attachment.description?.toLowerCase().includes(q) ||
          attachment.fk_table?.toLowerCase().includes(q)
        )
      }
      return true
    })
  }, [attachments, selectedEntityType, entityId, searchQuery])

  // ─── Handlers ─────────────────────────────────────────────────────────────

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`File exceeds the ${MAX_FILE_SIZE_MB}MB limit. Please select a smaller file.`)
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    setUploadFile(file)
    setUploadTitle(file.name.replace(/\.[^/.]+$/, ''))
    setUploadDialogOpen(true)
  }

  async function handleUpload() {
    if (!uploadFile) return

    const params: {
      file: File
      title?: string
      description?: string
      fk_table?: string
      fk_id?: number
    } = {
      file: uploadFile,
      title: uploadTitle || undefined,
      description: uploadDescription || undefined,
    }

    // Only attach to entity if both type and ID are provided
    if (uploadEntityType && uploadEntityId) {
      params.fk_table = uploadEntityType
      params.fk_id = parseInt(uploadEntityId, 10)
    }

    try {
      await uploadMutation.mutateAsync(params)
      toast.success(`${uploadFile.name} uploaded successfully.`)
      resetUploadDialog()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload file')
    }
  }

  function resetUploadDialog() {
    setUploadDialogOpen(false)
    setUploadFile(null)
    setUploadTitle('')
    setUploadDescription('')
    setUploadEntityType('')
    setUploadEntityId('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function handleEdit(attachment: Attachment) {
    setEditingAttachment(attachment)
    setEditTitle(attachment.title || '')
    setEditDescription(attachment.description || '')
    setEditDialogOpen(true)
  }

  async function handleUpdateAttachment() {
    if (!editingAttachment) return
    try {
      await updateMutation.mutateAsync({
        attachment_id: editingAttachment.id,
        title: editTitle,
        description: editDescription,
      })
      toast.success('File details updated successfully.')
      setEditDialogOpen(false)
      setEditingAttachment(null)
      setEditTitle('')
      setEditDescription('')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update file')
    }
  }

  async function handleDownload(attachment: Attachment) {
    try {
      await downloadAttachment(attachment.id, attachment.file_name)
      toast.success(`Downloading ${attachment.file_name}...`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to download file')
    }
  }

  function handlePreview(attachment: Attachment) {
    setPreviewAttachment(attachment)
    setPreviewDialogOpen(true)
  }

  function handleDeleteClick(attachment: Attachment) {
    setDeletingAttachment(attachment)
    setDeleteDialogOpen(true)
  }

  async function handleDelete() {
    if (!deletingAttachment) return
    try {
      await deleteMutation.mutateAsync({
        attachment_id: deletingAttachment.id,
        fk_table: deletingAttachment.fk_table,
        fk_id: deletingAttachment.fk_id,
      })
      toast.success(`${deletingAttachment.file_name} deleted successfully.`)
      setDeleteDialogOpen(false)
      setDeletingAttachment(null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete file')
    }
  }

  function clearFilters() {
    setSelectedEntityType('')
    setEntityId('')
    setSearchQuery('')
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <FilesIcon className="h-8 w-8" />
            File Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Upload and manage attachments for test cases, plans, requirements, and other entities
          </p>
        </div>
        <Button onClick={() => fileInputRef.current?.click()}>
          <Upload className="mr-2 h-4 w-4" />
          Upload File
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileSelect}
          accept={ACCEPTED_FILE_TYPES}
        />
      </div>

      {/* Error State */}
      {isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>Failed to load files: {error instanceof Error ? error.message : 'Unknown error'}</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RotateCcw className="mr-2 h-3 w-3" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      {!isLoading && attachments && attachments.length > 0 && (
        <FileStats attachments={attachments} />
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filters
              </CardTitle>
              <CardDescription>
                Filter files by entity type, entity ID, or search by name
              </CardDescription>
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <RotateCcw className="mr-2 h-3 w-3" />
                Clear Filters
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Entity Type Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Entity Type</label>
              <Select value={selectedEntityType} onValueChange={setSelectedEntityType}>
                <SelectTrigger>
                  <SelectValue placeholder="All entity types" />
                </SelectTrigger>
                <SelectContent>
                  {ENTITY_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Entity ID Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Entity ID</label>
              <Input
                type="number"
                placeholder="All entities"
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
                min="1"
              />
            </div>

            {/* Search */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by filename, title, description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Files Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            {isLoading
              ? 'Loading files...'
              : filteredAttachments.length > 0
                ? `Showing ${filteredAttachments.length} of ${attachments?.length || 0} files${hasActiveFilters ? ' (filtered)' : ''}`
                : 'No files found'}
          </CardTitle>
          <CardDescription>
            {selectedEntityType && entityId
              ? `Files attached to ${getEntityLabel(selectedEntityType)} #${entityId}`
              : selectedEntityType
                ? `Files attached to ${getEntityLabel(selectedEntityType)}`
                : 'All files in your organization'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <FilesTableSkeleton />
          ) : filteredAttachments.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>File</TableHead>
                    <TableHead>Title / Description</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Storage</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAttachments.map((attachment) => (
                    <TableRow key={attachment.id}>
                      {/* File Name + Icon */}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getFileIcon(attachment.file_type)}
                          <span className="font-medium truncate max-w-[200px]" title={attachment.file_name}>
                            {attachment.file_name}
                          </span>
                        </div>
                      </TableCell>

                      {/* Title / Description */}
                      <TableCell>
                        <div>
                          <div className="font-medium">{attachment.title || '—'}</div>
                          {attachment.description && (
                            <div className="text-sm text-muted-foreground truncate max-w-xs" title={attachment.description}>
                              {attachment.description}
                            </div>
                          )}
                        </div>
                      </TableCell>

                      {/* Entity Association */}
                      <TableCell>
                        {attachment.fk_table ? (
                          <div className="space-y-1">
                            <Badge variant="outline" className="text-xs">
                              {getEntityLabel(attachment.fk_table)}
                            </Badge>
                            {attachment.fk_id > 0 && (
                              <div className="text-xs text-muted-foreground">
                                #{attachment.fk_id}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">Unattached</span>
                        )}
                      </TableCell>

                      {/* Size */}
                      <TableCell>
                        <Badge variant="secondary">{formatFileSize(attachment.file_size)}</Badge>
                      </TableCell>

                      {/* Storage Type */}
                      <TableCell>
                        <Badge variant="outline">{attachment.storage_type?.toUpperCase() || 'GCS'}</Badge>
                      </TableCell>

                      {/* Uploaded Date */}
                      <TableCell>{formatDate(attachment.created_at)}</TableCell>

                      {/* Actions */}
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {attachment.file_type?.startsWith('image/') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePreview(attachment)}
                              title="Preview image"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownload(attachment)}
                            title="Download file"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(attachment)}
                            title="Edit details"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(attachment)}
                            title="Delete file"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <FilesIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium mb-1">
                {hasActiveFilters ? 'No files match your filters' : 'No files uploaded yet'}
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                {hasActiveFilters
                  ? 'Try adjusting your filter criteria or clear all filters.'
                  : 'Upload files to attach them to test cases, requirements, and other entities.'}
              </p>
              {hasActiveFilters ? (
                <Button variant="outline" onClick={clearFilters}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Clear Filters
                </Button>
              ) : (
                <Button onClick={() => fileInputRef.current?.click()}>
                  <Upload className="mr-2 h-4 w-4" />
                  Upload File
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Upload Dialog ──────────────────────────────────────────────────── */}
      <Dialog open={uploadDialogOpen} onOpenChange={(open) => { if (!open) resetUploadDialog() }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload File</DialogTitle>
            <DialogDescription>
              Upload a file to your organization. Optionally attach it to a specific entity.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* File Preview */}
            <div>
              <label className="text-sm font-medium">File</label>
              <div className="mt-2 p-3 bg-muted rounded-md">
                <div className="flex items-center gap-2">
                  {uploadFile && getFileIcon(uploadFile.type)}
                  <span className="font-medium">{uploadFile?.name}</span>
                </div>
                {uploadFile && (
                  <div className="text-sm text-muted-foreground mt-1">
                    {formatFileSize(uploadFile.size)}
                  </div>
                )}
              </div>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Title (Optional)</label>
              <Input
                placeholder="Enter a descriptive title"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Description (Optional)</label>
              <Textarea
                placeholder="Enter file description"
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                rows={3}
              />
            </div>

            {/* Entity Attachment */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Attach to Entity (Optional)</p>
              <div className="space-y-3">
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Entity Type</label>
                  <Select value={uploadEntityType} onValueChange={setUploadEntityType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select entity type (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {ENTITY_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Entity ID</label>
                  <Input
                    type="number"
                    placeholder="Enter entity ID (optional)"
                    value={uploadEntityId}
                    onChange={(e) => setUploadEntityId(e.target.value)}
                    min="1"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetUploadDialog}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={uploadMutation.isPending}>
              {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Edit Dialog ────────────────────────────────────────────────────── */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit File Details</DialogTitle>
            <DialogDescription>
              Update the title and description for "{editingAttachment?.file_name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Title</label>
              <Input
                placeholder="Enter file title"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <Textarea
                placeholder="Enter file description"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateAttachment} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Updating...' : 'Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Preview Dialog ─────────────────────────────────────────────────── */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{previewAttachment?.file_name}</DialogTitle>
            <DialogDescription>
              {previewAttachment?.title || 'Image preview'}
              {previewAttachment && ` — ${formatFileSize(previewAttachment.file_size)}`}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[600px] overflow-auto rounded-md border bg-muted/30">
            {previewAttachment?.download_url ? (
              <img
                src={previewAttachment.download_url}
                alt={previewAttachment.file_name}
                className="w-full h-auto"
              />
            ) : (
              <div className="flex items-center justify-center h-64 text-muted-foreground">
                <p>Preview not available. Use the download button to view this file.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            {previewAttachment && (
              <Button variant="outline" onClick={() => handleDownload(previewAttachment)}>
                <Download className="mr-2 h-4 w-4" />
                Download
              </Button>
            )}
            <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Delete Confirmation Dialog ─────────────────────────────────────── */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete File</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingAttachment?.file_name}"?
              This action cannot be undone and will remove the file from storage permanently.
            </DialogDescription>
          </DialogHeader>
          {deletingAttachment?.fk_table && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This file is attached to {getEntityLabel(deletingAttachment.fk_table)} #{deletingAttachment.fk_id}.
                Deleting it will remove the attachment reference.
              </AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
